// ─────────────────────────────────────────────
//  QRPrint — print template picker
// ─────────────────────────────────────────────
import { useState } from "react";
import QRCode from "../components/QRCode";
import { PLATFORMS } from "../lib/data";

function ReceiptStrip({ business, qrVal }) {
  return (
    <div style={{ width: 280, background: "white", padding: "22px 20px", fontFamily: "inherit", boxShadow: "0 4px 24px rgba(0,0,0,0.12)", borderRadius: 12 }}>
      <div style={{ textAlign: "center", borderBottom: "1px dashed #e0e0e0", paddingBottom: 16, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#333" }}>{business.name}</div>
        <div style={{ fontSize: 11, color: "#aaa", marginTop: 2 }}>📍 {business.location}</div>
      </div>
      <div style={{ textAlign: "center", marginBottom: 14 }}>
        <div style={{ fontSize: 11, color: "#888", marginBottom: 10, fontWeight: 500 }}>⚡ Quick Review</div>
        <div style={{ display: "inline-block", padding: 8, background: "white", border: "1.5px solid #f0ede8", borderRadius: 10 }}>
          <QRCode value={qrVal} size={110} color="#1a1a2e" />
        </div>
        <div style={{ fontSize: 10, color: "#bbb", marginTop: 8 }}>Scan to leave a review</div>
        <div style={{ fontSize: 9, color: "#ddd", marginTop: 2 }}>jiffyreview.com</div>
      </div>
      <div style={{ textAlign: "center", borderTop: "1px dashed #e0e0e0", paddingTop: 12 }}>
        <div style={{ display: "flex", justifyContent: "center", gap: 10 }}>
          {[["G","#4285F4"],["Y","#D32323"],["T","#00AF87"]].map(([l,c]) => (
            <div key={l} style={{ width: 20, height: 20, borderRadius: 5, background: c, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 10, fontWeight: 800 }}>{l}</div>
          ))}
        </div>
        <div style={{ fontSize: 9, color: "#ccc", marginTop: 6 }}>Reviews posted to Google, Yelp & TripAdvisor</div>
      </div>
    </div>
  );
}

function TableCard({ business, qrVal }) {
  return (
    <div style={{ width: 340, background: "linear-gradient(135deg,#FF6B35,#FFBE0B)", padding: "28px 26px", borderRadius: 18, fontFamily: "inherit", boxShadow: "0 12px 40px rgba(255,107,53,0.35)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.7)", letterSpacing: "0.1em", marginBottom: 4 }}>LEAVE A REVIEW</div>
          <div style={{ fontSize: 20, fontWeight: 900, color: "white", lineHeight: 1.1 }}>{business.name}</div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.7)", marginTop: 4 }}>📍 {business.location}</div>
        </div>
        <div style={{ fontSize: 32 }}>{business.emoji}</div>
      </div>
      <div style={{ background: "white", borderRadius: 14, padding: 14, display: "flex", gap: 14, alignItems: "center" }}>
        <QRCode value={qrVal} size={90} color="#1a1a2e" />
        <div>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#333", marginBottom: 4 }}>Scan me!</div>
          <div style={{ fontSize: 11, color: "#888", lineHeight: 1.5, marginBottom: 8 }}>Takes just 20 seconds. Your review goes to:</div>
          <div style={{ display: "flex", gap: 5 }}>
            {[["G","#4285F4"],["Y","#D32323"],["T","#00AF87"]].map(([l,c]) => (
              <div key={l} style={{ width: 18, height: 18, borderRadius: 4, background: c, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 9, fontWeight: 800 }}>{l}</div>
            ))}
          </div>
        </div>
      </div>
      <div style={{ textAlign: "center", marginTop: 12, fontSize: 10, color: "rgba(255,255,255,0.6)" }}>
        Powered by ⚡ JiffyReview.com
      </div>
    </div>
  );
}

function MinimalCard({ business, qrVal }) {
  return (
    <div style={{ width: 300, background: "#1a1a2e", padding: "28px 24px", borderRadius: 16, fontFamily: "inherit", textAlign: "center" }}>
      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", letterSpacing: "0.15em", marginBottom: 6 }}>ENJOYED YOUR VISIT?</div>
      <div style={{ fontSize: 20, fontWeight: 900, color: "white", marginBottom: 4 }}>{business.name}</div>
      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", marginBottom: 20 }}>Tell others about it ↓</div>
      <div style={{ background: "white", display: "inline-block", padding: 10, borderRadius: 12, marginBottom: 14 }}>
        <QRCode value={qrVal} size={120} color="#1a1a2e" />
      </div>
      <div style={{ fontSize: 28, marginBottom: 6 }}>★★★★★</div>
      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", marginBottom: 12 }}>Scan · Rate · Done in 20 seconds</div>
      <div style={{ fontSize: 9, color: "rgba(255,255,255,0.25)" }}>⚡ jiffyreview.com</div>
    </div>
  );
}

const TEMPLATE_META = {
  receipt: { label: "🧾 Receipt Strip", format: "58mm receipt strip",       size: "58 × 100mm" },
  card:    { label: "🎨 Table Card",    format: "85×55mm business card",     size: "85 × 55mm"  },
  minimal: { label: "⬛ Minimal",       format: "90×60mm table tent insert", size: "90 × 60mm"  },
};

export default function QRPrint({ business }) {
  const [style, setStyle] = useState("receipt");
  const qrVal = `jiffyreview.com/r/${business.name.toLowerCase().replace(/\s+/g, "-")}`;

  const templates = { receipt: <ReceiptStrip business={business} qrVal={qrVal} />, card: <TableCard business={business} qrVal={qrVal} />, minimal: <MinimalCard business={business} qrVal={qrVal} /> };

  return (
    <div>
      <h1 style={{ margin: "0 0 6px", fontSize: 26, fontWeight: 900, letterSpacing: "-0.03em" }}>
        QR & Print Templates
      </h1>
      <p style={{ margin: "0 0 28px", color: "rgba(255,255,255,0.4)", fontSize: 13 }}>
        Three formats — print-ready for every placement.
      </p>

      {/* Template switcher */}
      <div style={{ display: "flex", gap: 8, marginBottom: 28 }}>
        {Object.entries(TEMPLATE_META).map(([id, meta]) => (
          <button
            key={id}
            onClick={() => setStyle(id)}
            style={{
              padding: "8px 16px", borderRadius: 10,
              border: `1.5px solid ${style === id ? "#FF6B35" : "rgba(255,255,255,0.1)"}`,
              background: style === id ? "rgba(255,107,53,0.15)" : "transparent",
              color: style === id ? "#FF6B35" : "rgba(255,255,255,0.4)",
              fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
            }}
          >
            {meta.label}
          </button>
        ))}
      </div>

      <div style={{ display: "flex", gap: 40, alignItems: "flex-start", flexWrap: "wrap" }}>
        {/* Template preview */}
        <div>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", marginBottom: 12, letterSpacing: "0.1em" }}>PREVIEW</div>
          {templates[style]}
        </div>

        {/* Print info */}
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", marginBottom: 12, letterSpacing: "0.1em" }}>PRINT DETAILS</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
            {[
              { label: "Format",     val: TEMPLATE_META[style].format },
              { label: "Print Size", val: TEMPLATE_META[style].size },
              { label: "Resolution", val: "300dpi print-ready" },
              { label: "QR URL",     val: qrVal },
              { label: "Platforms",  val: "Google · Yelp · TripAdvisor" },
            ].map(({ label, val }) => (
              <div key={label} style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 10, padding: "12px 14px" }}>
                <div style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", marginBottom: 3 }}>{label}</div>
                <div style={{ fontSize: 13, color: "#e0ddd8", wordBreak: "break-all" }}>{val}</div>
              </div>
            ))}
          </div>

          <button
            style={{
              width: "100%", padding: 13, borderRadius: 12, border: "none",
              background: "linear-gradient(135deg,#FF6B35,#FFBE0B)",
              color: "white", fontSize: 14, fontWeight: 700,
              cursor: "pointer", fontFamily: "inherit", marginBottom: 8,
            }}
          >
            ↓ Download Print File
          </button>
          <button
            style={{
              width: "100%", padding: 12, borderRadius: 12,
              border: "1px solid rgba(255,255,255,0.1)",
              background: "transparent", color: "rgba(255,255,255,0.4)",
              fontSize: 13, cursor: "pointer", fontFamily: "inherit",
            }}
          >
            Order Printed Cards →
          </button>
        </div>
      </div>
    </div>
  );
}
